/**
 * *Your day*'s one read — `GET /api/v1/overview` (FR-L5, T-6.20; waves.md
 * interface I-2, C14 §10.6 item 6).
 *
 * Each part is `null` when the caller does not hold that resource's
 * `can_view` (the card is then absent), `{ error: true }` when the service
 * could not read it (the card says so and shows no figure), or the figures —
 * already narrowed server-side exactly as the part's own list narrows (FR-L4).
 * **Nothing here re-derives who may see what**; `scope` is the server saying
 * whether it counted the firm or the caller.
 */

import { api } from '../api'

/** A part the service could not read. No figure rides with it. */
export interface PartFailed {
  error: true
  reason?: string
}

export type Scope = 'firm' | 'mine'

export interface CasesPart {
  scope: Scope
  open: number
  overdue: number
  /** The *Mine* queue — cases the caller is on or reviewing. */
  mine: number
}

export interface TasksPart {
  scope: Scope
  due_today: number
  due_this_week: number
  late: number
}

export interface CalendarPart {
  due_today: number
  due_this_week: number
  overdue: number
}

export interface MessagesPart {
  unread_conversations: number
  unread_messages: number
}

export interface InvoicesPart {
  unpaid: number
  overdue: number
}

export interface LeadsPart {
  scope: Scope
  new: number
  follow_up_due: number
}

/** C25, ADR-0052 §7 (Wave 27B, T-6.22) — bookings whose payment proof waits
 *  for the firm (`payment_review`, *Proof to check*). */
export interface BookingsPart {
  to_check: number
}

export type Part<T> = T | PartFailed | null

export interface Overview {
  cases: Part<CasesPart>
  tasks: Part<TasksPart>
  calendar: Part<CalendarPart>
  messages: Part<MessagesPart>
  invoices: Part<InvoicesPart>
  leads: Part<LeadsPart>
  /** w27b — optional only so a fixture written before it still types; the
   *  service sends it from Wave 27 (`null` without `booking:can_view`). */
  bookings?: Part<BookingsPart>
}

export type OverviewKey = keyof Overview

export function partFailed(part: unknown): part is PartFailed {
  return typeof part === 'object' && part !== null && (part as PartFailed).error === true
}

export const overview = {
  read: () => api<Overview>('/api/v1/overview'),
}
